import { Skeleton } from "../components/ui/skeleton";
import {
  DownloadIcon,
  ExpandIcon,
  FavoritesIcon,
  IngredientsIcon,
  InstructionsIcon,
  LeftArrowIcon,
  LinkIcon,
  MiniMize,
  PrepTimeIcon,
  PrintIcon,
} from "../components";
import { Link } from "react-router-dom";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import React, { useState, useRef, useEffect, forwardRef } from "react";
import PortionsIcon from "../components/icons/PortionsIcon";
import TotalTimeIcon from "../components/icons/TotalTimeIcon";

const HtmlRenderer = ({ className, htmlContent }) => {
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

const CCrecipe = forwardRef((Content, ref) => {
  // const data = Content.data;
  // const loading = Content.loading;
  // const handleToggleFavorite = Content.handleToggleFavorite;
  // const handlePrint = handlePrint
  const {
    data,
    loading,
    handleToggleFavorite,
    handlePrint,
    printTopRef,
    printBottomRef,
    printMidRef,
  } = Content

  // Toggle popup function
  const togglePopup = () => {
    if (isPopupVisible) {
      setIsClosing(true);
      setTimeout(() => {
        setIsPopupVisible(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsPopupVisible(true);
    }
  };

  const { copied, copyToClipboard } = useCopyToClipboard();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  return (
    <div className="h-full" ref={ref}>
      <div className="flex flex-col h-full px-8 lg:px-0 lg:pr-8 lg:flex-row">
        {/* Left Sidebar */}
        <div className="w-full h-auto border-b border-gray-100 lg:w-1/3 lg:border-r lg:border-b-0">
          <div className="flex justify-between px-4 py-8 lg:px-6 lg:pl-14">
            <Link
              className="flex items-center gap-3 group"
              to="/culture-and-customs"
            >
              <div className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 print:hidden">
                <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />
              </div>
              <h5 className="print:hidden text-base text-gray-600 group-hover:text-gold-500">
                Back to Culture & Customs
              </h5>
            </Link>
          </div>
          <div className="px-4 lg:pl-12 pb-8">
            {data.imageUrl && (
              <div ref={printTopRef} className="relative w-full object-cover rounded-3xl">
                <img
                  className="object-cover w-full h-full rounded-3xl max-h-44 mb-8"
                  src={data.imageUrl}
                  alt={data.title}
                />
                {/* Expand Icon for Popup */}
                <div
                  className="absolute flex items-center justify-center transition rounded-full cursor-pointer bottom-4 right-4 h-11 w-11 bg-grayTransparent hover:scale-110"
                  onClick={togglePopup}
                >
                  <ExpandIcon className="text-white" />
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-gray-900 pb-0.5">
              {data.title}
            </h2>
            <div className="flex flex-row flex-wrap gap-2 mt-3">
              {loading ? (
                <Skeleton className="w-1/3 h-4 rounded" />
              ) : (
                data?.categories &&
                data.categories.map((category, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs bg-gold-50 text-gold-500 rounded-xl"
                  >
                    {category}
                  </span>
                ))
              )}
              {/* CatList (Seasons and Nationalities) */}
              {data?.catList &&
                data.catList.map((cat, index) => (
                  <span
                    key={`cat-${index}`}
                    className="px-2 py-1 text-xs font-medium leading-4 bg-gold-50 text-gold-500 rounded-27"
                  >
                    {cat}
                  </span>
                ))}
            </div>

            {data?.description && (
              <div className="mt-3" ref={printMidRef}>
                <div 
                  className="text-gray-700 text-base"
                  dangerouslySetInnerHTML={{
                    __html: `<p>${
                      data.description
                        .replace(/<\/p>\s*<p[^>]*>/g, " ") // Merge multiple <p> tags
                        .replace(/<\/?p[^>]*>/g, "") // Remove remaining <p> tags
                        .replace(/<br\s*\/?>/gi, " ") // Remove <br> tags
                        .replace(/(\.\.\.|&hellip;)?\s*Read\s*more.*$/i, "")
                        .trim() || "No Description Available"
                    }</p>`,
                  }}
                />
              </div>
            )}
            {/* <p className="text-gray-800">{data.description}</p> */}
          </div>

          {(data?.ps_prep_time || data?.ps_total_time || data?.ps_portions) && (
            <div ref={printBottomRef} className="border-t-1 border-gray-100 py-8">
              <div className="flex items-center xl:justify-center 2xl:justify-between flex-wrap px-4 lg:pl-12">
                {data?.ps_prep_time && (
                  <>
                    <div className="flex justify-center w-full xs:w-1/3 lg:w-full xl:w-1/2 2xl:w-1/3 px-4 xs:border-r lg:border-0 xl:border-r border-gray-100">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-gray-600 w-6 h-6 flex items-center justify-center">
                          <PrepTimeIcon />
                        </div>
                        <p className="uppercase font-medium text-sm leading-4.5 text-gray-700">
                          Prep time
                        </p>
                        <p className="uppercase font-normal text-sm leading-4.5 text-gray-600">
                          {data?.ps_prep_time}
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {data?.ps_total_time && (
                  <>
                    <div className="flex justify-center w-full xs:w-1/3 lg:w-full xl:w-1/2 2xl:w-1/3 mt-6 pt-6 px-4 xs:pt-0 xs:mt-0 lg:mt-6 lg:pt-6 xl:mt-0 xl:pt-0 border-t xs:border-t-0 xs:border-r lg:border-t xl:border-t-0 lg:border-r-0  2xl:border-r border-gray-100">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-gray-600 w-6 h-6 flex items-center justify-center">
                          <TotalTimeIcon />
                        </div>
                        <p className="uppercase font-medium text-sm leading-4.5 text-gray-700">
                          Total time
                        </p>
                        <p className="uppercase font-normal text-sm leading-4.5 text-gray-600">
                          {data?.ps_total_time}
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {data?.ps_portions && (
                  <div className="flex justify-center w-full xs:w-1/3 lg:w-full xl:w-1/2 2xl:w-1/3 mt-6 pt-6 border-t xs:border-t-0 xs:pt-0 xs:mt-0 px-4 lg:border-t 2xl:border-t-0 lg:pt-6 2xl:pt-0 border-gray-100 lg:mt-6 2xl:mt-0">
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-gray-600 w-6 h-6 flex items-center justify-center">
                        <PortionsIcon />
                      </div>
                      <p className="uppercase font-medium text-sm leading-4.5 text-gray-700">
                        Portions
                      </p>
                      <p className="uppercase font-normal text-sm leading-4.5 text-gray-600">
                        {data?.ps_portions}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border-t-1 border-gray-100 pt-8">
            <div className="px-4 lg:pl-12">
              <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">
                Author:
              </h5>
              <div className="flex flex-row items-center gap-2 mb-8">
                <img src="/favicon.png" alt="Favicon" className="w-7 h-7" />
                <p className="font-medium text-sm leading-4.5 text-gray-700">
                  {data?.author}
                </p>
              </div>
            </div>

            {data?.ps_source_attribution && (
              <div className="px-4 lg:pl-12 mb-8">
                <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">
                  Source:
                </h5>
                <div className="rounded-xl bg-gold-50 p-4 text-base leading-5 text-gray-900">
                  {data?.ps_source_attribution}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Image Section */}
        <div className="w-full h-auto px-4 pt-8 pb-8 lg:w-2/3 lg:pt-0 xl:px-12">
          <div className="justify-end hidden gap-2 pt-8 pb-8 lg:flex">
            {loading || !data ? (
              <Skeleton className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group" />
            ) : (
              <button
                key={data.favorite} //  Forces re-render when favorite state changes
                type="button"
                onClick={(e) => handleToggleFavorite(e, data.id)}
                className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
              >
                <FavoritesIcon
                  filled={Boolean(data?.favorite)}
                  className={`w-7 h-7 transition-colors duration-200 ${
                    data?.favorite
                      ? "text-red-500"
                      : "text-gray-500 hover:text-red-500"
                  }`}
                />
              </button>
            )}
            <a
              href="#"
              className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
            >
              <DownloadIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
            </a>
            <button
              type="button"
              onClick={handlePrint} // <- must be passed from parent
              className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
            >
              <PrintIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
            </button>

            <a
              href="#"
              className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
              onClick={(e) => {
                e.preventDefault(); // Prevent navigation
                copyToClipboard();
              }}
            >
              <LinkIcon
                className={`text-gray-700 w-7 h-7 group-hover:text-gold-500 ${copied ? "text-green-500 group-hover:text-green-500" : ""}`}
              />
            </a>
          </div>
          <div className="text-gray-700 text-lg">
            {data.ps_ingredients ? (
              <div>
                <h5 className="mb-6 text-base font-semibold leading-5 text-gray-900 flex gap-3 items-center">
                  <span>
                    <IngredientsIcon />
                  </span>
                  Ingredients
                </h5>
                <HtmlRenderer
                  className="[&>h2]:font-bold [&>h2]:text-base [&>h2]:leading-5 [&>h2]:text-gray-900 [&>h2]:mb-2 text-18 leading-7 [&>p]:mb-6 [&>ul]:list-disc [&>ul]:ml-7 text-gray-700 [&>ul]:mb-8 [&>ul>li]:mb-3"
                  htmlContent={data.ps_ingredients}
                />
              </div>
            ) : (
              "No Description Available"
            )}
          </div>
          <div className="text-gray-700 text-lg">
            {data.ps_instructions ? (
              <div className="mt-12">
                <h5 className="mb-6 text-base font-semibold leading-5 text-gray-900 flex gap-3 items-center">
                  <span>
                    <InstructionsIcon />
                  </span>
                  Instructions
                </h5>
                <HtmlRenderer
                  className="[&>h2]:font-bold [&>h2]:text-base [&>h2]:leading-5 [&>h2]:text-gray-900 [&>h2]:mb-2 text-18 leading-7 [&>p]:mb-6 [&>ul]:list-disc [&>ul]:ml-7 text-gray-700 [&>ul]:mb-8 [&>ul>li]:mb-3"
                  htmlContent={data.ps_instructions}
                />
              </div>
            ) : (
              "No Description Available"
            )}
          </div>
          <div
            className="text-gray-700 text-lg"
            dangerouslySetInnerHTML={{
              __html: `<p>${(data.about || "No Description Available")
                .replace(/<\/p>\s*<p[^>]*>/g, " ") // Merge content between <p> tags
                .replace(/<\/?p[^>]*>/g, "") // Remove all remaining <p> tags
                .replace(/<br\s*\/?>/gi, " ") // Remove <br> or <br /> tags
                .trim()}</p>`,
            }}
          />
        </div>
      </div>

      {/* Fullscreen Popup */}
      {isPopupVisible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70"
          onMouseDown={togglePopup}
        >
          <div
            className={`relative max-w-[1040px] max-h-[616px] w-full h-full ${
              isClosing ? "animate-popupZoomIn" : "animate-popupZoomOut"
            }`}
          >
            <img
              className="object-cover w-full h-full rounded-3xl"
              src={data.imageUrl}
              alt={data.title}
            />
            {/* Minimize Button */}
            <button
              className="absolute flex items-center justify-center transition rounded-full bottom-4 right-4 h-11 w-11 bg-grayTransparent hover:scale-90"
              onClick={togglePopup}
            >
              <MiniMize className="text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
export default CCrecipe;
